import { useNavigate, useParams } from "react-router-dom";
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import SearchField from "../../../forms/search-field";
import { customerListAction } from "../../../../redux/actions/general-action";
import { purchaseProductListAction, purchaseProductDetailsAction, goodsTempProVeriAction } from "../../../../redux/actions/purchase-action";
import { useDispatch } from "react-redux";
import AddButton from "../../../buttons/button";
import { Database, DownloadIcon, FileJson, Hourglass, Layers, Pencil } from "lucide-react";
import DynamicTransactionTable from "../../../tables/dynamic-transaction-table";
import VMProductSidePanel from "./vendor-mapping-new-product-side-panel";
import SelectUOMModal from "./uom-mapping-modal";
import { uomDropDownAction } from "../../../../redux/actions/settings-action";
import errorToast from "../../../helper-components/error-toast";
import Papa from "papaparse";
import excelImg from "../../../../assets/icons/excel.png"
import { downloadAllTemplates, downloadAllTemplatesJson } from "../../../helper-components/helper-function";
import { VendorMappingAction, getVendorMappedAction } from "../../../../redux/actions/settings-action";
import successToast from "../../../helper-components/success-toast";
import { initUploadDocumentsGrn } from "../../../purchase-section/goods-receipt/create-edit-view-grn";
const StatCard = ({ icon: Icon, value, label, tone = "success" }) => {
  const toneMap = {
    success: {
      wrap: "bg-[var(--trans-stats-con-green-bg)] border-[var(--trans-stats-con-green-fg)]",
      iconBox: "bg-[var(--trans-stats-con-green-fg)]",
      text: "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-green-fg)]",
    },
    danger: {
      wrap: "bg-[var(--trans-stats-con-orange-bg)] border-[var(--trans-stats-con-orange-fg)]",
      iconBox: "bg-[var(--trans-stats-con-orange-fg)]",
      text: "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-orange-fg)]",
    },
    info: {
      wrap: "bg-[var(--trans-stats-con-blue-bg)] border-[var(--trans-stats-con-voilet2-fg)]",
      iconBox: "bg-[var(--trans-stats-con-voilet2-fg)]",
      text: "text-[length:var(--trans-stats-con-desc-fs)] font-[var(--trans-stats-con-desc-fw)] text-[var(--trans-stats-con-blue-fg)]",
    },
  };

  const t = toneMap[tone] ?? toneMap.info;

  return (
    <div className={`flex items-center gap-3 rounded-[12px] border px-4 py-3 w-full sm:w-auto min-w-0 sm:min-w-[170px] ${t.wrap}`}>
      <div className={`h-10 w-10 rounded-[10px] flex items-center justify-center text-white ${t.iconBox}`}>
        <Icon size={18} />
      </div>
      <div className="leading-tight min-w-0">
        <div className="text-[18px] font-bold text-black truncate">{value}</div>
        <div className={`text-[12px] font-semibold ${t.text} truncate`}>{label}</div>
      </div>
    </div>
  );
};

// ── Static class strings (module scope so they are not rebuilt every render) ──
const CHIP_CLS =
  "px-2 py-[3px] rounded-[8px] text-[12px] bg-[var(--badge-bg, #F3F5F9)] text-[var(--badge-fg, #2b2f38)] border border-[var(--badge-border, #E6EAF2)]";
const INPUT_CLS =
  "w-full h-[38px] px-2 border border-[var(--search-input-border)] rounded-[6px] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] placeholder:text-[var(--search-place-color)]";

// Stable empty reference so memoized UOM cells of empty rows don't re-render
// just because a fresh [] was created each render.
const EMPTY_UOMS = [];

/**
 * Vendor UOM chips cell.
 * Memoized: only re-renders when its row's `uoms` (or add affordance) changes.
 */
const VendorUomCell = memo(function VendorUomCell({ rowKey, uoms, allowAdd, onAdd }) {
  const firstTwo = uoms.slice(0, 2);
  const extra = uoms.length > 2 ? uoms.length - 2 : 0;
  return (
    <div
      className="flex items-center gap-2 min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
    >
      {firstTwo.map((u) => (
        <span key={u} className={CHIP_CLS}>
          {u}
        </span>
      ))}
      {extra ? <span className="text-primary text-[12px] font-semibold">+{extra}</span> : null}
      {allowAdd && uoms.length === 0 && (
        <button
          type="button"
          className="text-[13px] text-[var(--description-color,#2B2F38)] opacity-60 hover:underline whitespace-nowrap"
          onClick={() => onAdd(rowKey, uoms)}
        >
          Click to Add UOM
        </button>
      )}
    </div>
  );
});

/**
 * System (mapped) UOM cell — shows mapped system UOM chips and the edit affordance.
 * Memoized on (rowKey, uoms, uomMemo, onEdit); `uomMemo` only changes when a
 * mapping is actually edited, so plain keystrokes elsewhere don't re-render it.
 */
const SystemUomCell = memo(function SystemUomCell({ rowKey, uoms, uomMemo, onEdit }) {
  const mapped = uoms.filter((vu) => uomMemo[vu]?.item_id);
  const firstTwoMapped = mapped.slice(0, 2);
  const rem = mapped.length > 2 ? mapped.length - 2 : 0;
  return (
    <div
      className="flex items-center justify-between gap-2 min-w-[240px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
        {uoms.length === 0 ? (
          <span className="text-[12px] text-[#6B7280]">No UOMs</span>
        ) : mapped.length === 0 ? (
          <button
            type="button"
            className="text-[13px] text-[var(--description-color,#2B2F38)] hover:underline whitespace-nowrap"
            onClick={() => onEdit(rowKey, uoms)}
          >
            Click to map UOM
          </button>
        ) : (
          <>
            {firstTwoMapped.map((vu) => (
              <span
                key={vu}
                className={CHIP_CLS}
                title={`${vu} → ${uomMemo[vu]?.uom_name || ""}`}
              >
                {uomMemo[vu]?.uom_name}
              </span>
            ))}
            {rem ? <span className="text-primary text-[12px] font-semibold">+{rem}</span> : null}
          </>
        )}
      </div>
      <button
        type="button"
        className="shrink-0 h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center hover:opacity-90"
        onClick={() => onEdit(rowKey, uoms)}
        title="Edit UOM Mapping"
      >
        <Pencil size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
});

// Pure stat computation extracted out of the JSX for clarity/reuse.
const computeStats = (mappingData, uomMemo) => {
  const allRows = Object.values(mappingData || {});
  const hasVendor = (r) => ((r.vendor_item_code || r.vendor_item_name) || "").toString().trim();

  const totalItems = allRows.filter(hasVendor).length;
  const itemsMapped = allRows.filter((r) => {
    if (!hasVendor(r)) return false;
    const sysMapped = !!r.system_item_id || (!!r.system_item_code && !!r.system_item_name);
    const uoms = r.vendor_uoms || [];
    const uomMapped = uoms.length > 0 ? uoms.every((vu) => !!uomMemo[vu]?.item_id) : false;
    return sysMapped || uomMapped;
  }).length;
  const itemsPending = Math.max(0, totalItems - itemsMapped);

  return { totalItems, itemsMapped, itemsPending };
};

const VendorItemMapping = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type, id } = useParams();
  // view/edit behaves like create (editable, blank row, import/save) — only the
  // vendor header is pre-filled from the saved mapping.
  const disabled = false;
  // Vendor header (code/name) for view/edit mode, loaded from the saved mapping.
  const [viewVendor, setViewVendor] = useState(null);
  const [uomMemo, setUomMemo] = useState({});
  const [selectedVendorUOMS,setSelectedVendorUOMS]=useState([]);
  const [uploadedVendorData, setUploadedVendorData] = useState({});
  const [open, setOpen] = useState(false);
  const [openUom, setOpenUom] = useState(false);
  const [selectedVendorRowKey, setSelectedVendorRowKey] = useState(null);
  const [uomOptions,setUOMOptions]=useState([]);
  // Per-row system UOM options, keyed by rowKey. Populated by calling
  // getItemAvailableStockAndUOM when a system item is selected for that row, so
  // the UOM-mapping dropdown shows only that item's available UOMs.
  const [rowUomOptions, setRowUomOptions] = useState({});
  // Latest mapping_data snapshot, kept in a ref so the stable openUomModalFor
  // handler can validate a row (vendor + system code mapped) without re-creating.
  const mappingDataRef = useRef({});
  useEffect(() => {
    dispatch(
      uomDropDownAction(
        null,
        (res) => setUOMOptions(res?.data || []),
        () => errorToast("Failed to fetch UOM")
      )
    );
  }, []);

  // View/Edit: load the saved vendor mapping for this vendor (id = cmr_id) only
  // to fill the vendor header (code/name). The table is left untouched.
  useEffect(() => {
    if (type === "new" || !id) return;
    dispatch(
      getVendorMappedAction(
        { vendor_id: id, page: 1, limit: 1 },
        (res) => {
          const rows = res?.data?.data || [];
          if (!rows.length) return;
          setViewVendor({
            cmr_id: id,
            cmr_code: rows[0].vendor_code || "",
            cmr_name: rows[0].vendor_name || "",
          });
        },
        () => {}
      )
    );
  }, [type, id, dispatch]);

  const vendorData = useMemo(
    () => ({
      ...uploadedVendorData
    }),
    [uploadedVendorData]
  );

  // Import now mirrors the GRN flow: parse the CSV, run it through the shared
  // GRN verify API to resolve/suggest system items + UOMs, then hand off to the
  // review page where the user confirms and triggers the vendor-mapping save.
  const processVendorMappingRows = (rows, values) => {
    if (!rows.length || !rows[0]) {
      errorToast("The file is empty or contains no valid records.");
      return;
    }

    dispatch(
      goodsTempProVeriAction(
        {
          cmr_id: values?.cmr_id,
          cmr_vendor_name: values?.cmr_name?.label,
          itemData: rows,
        },
        (res) => {
          navigate("/vendor-mapping-csv", {
            state: {
              data: res?.data,
              vendor: {
                cmr_id: values?.cmr_id,
                cmr_code: values?.cmr_code?.label || "",
                cmr_name: values?.cmr_name?.label || "",
              },
            },
          });
        },
        (err) => errorToast(err?.data?.message || "Import failed")
      )
    );
  };

  const handleUploadFile = async (event, values) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = null; // allow re-uploading the same file

    if (!values?.cmr_id) {
      errorToast("Please select a vendor before importing.");
      return;
    }

    if (file.name.toLowerCase().endsWith(".json")) {
      try {
        const rows = JSON.parse(await file.text());
        processVendorMappingRows(Array.isArray(rows) ? rows : [], values);
      } catch (err) {
        console.error("JSON parse failed:", err);
        errorToast("Error parsing file");
      }
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data || [];
        processVendorMappingRows(rows, values);
      },
      error: (err) => {
        console.error("CSV parse failed:", err);
        errorToast("Error parsing file");
      },
    });
  };
  const [newRows, setNewRows] = useState([{ rowKey: "new_1", vendor_item_code: "", vendor_item_name: "" }]);
  const [newRowSeq, setNewRowSeq] = useState(2);

  // ✅ Fit height
  const rootRef = useRef(null);
  const [availHeight, setAvailHeight] = useState(null);
  useLayoutEffect(() => {
    const calc = () => {
      const el = rootRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setAvailHeight(Math.max(0, window.innerHeight - top));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  // ✅ vendor search cache
  const [vendorOptionsData, setVendorOptionsData] = useState([]);

  const loadVendorOptions = (inputValue, field) =>
    new Promise((resolve) => {
      const onSuccess = (data) => {
        setVendorOptionsData(data.data || []);
        resolve([
          ...(data.data || []).map((item) => ({
            value: item.cmr_id,
            label: `${item?.cmr_code}-${item?.cmr_first_name}`,
          })),
          { value: "new_customer", label: <span className="text-primary">{"+ New Vendor"}</span> },
        ]);
      };

      const onError = () =>
        resolve([{ value: "new_customer", label: <span className="text-primary">{"+ New Vendor"}</span> }]);

      if (inputValue?.length > 0) {
        dispatch(customerListAction({ field, value: inputValue, isVendor: true }, onSuccess, onError));
      } else resolve([]);
    });

  /**
   * ✅ system search: use purchaseProductListAction
   * field = "item_code" | "item_name"
   *
   * IMPORTANT: do NOT allow SearchField's NoOptions CTA (which navigates).
   * So:
   * 1) We pass showCreateCTA={false} to SearchField (vendor mapping only)
   * 2) We always return at least one option: "+ New Customer" (alert hehe)
   */
  const loadSystemOptions = (inputValue, field) =>
    new Promise((resolve) => {
      const NEW_OPT = { value: "new_customer", label: <span className="text-primary">{"+ New Product"}</span>, raw: null };

      const onSuccess = (data) => {
        const rows = data?.data || [];
        const opts = rows.map((it) => ({
          value: it.item_id,
          label: field === "item_code" ? it.item_code : it.item_name,
          raw: it,
        }));
        resolve([...opts, NEW_OPT]);
      };

      const onError = () => resolve([NEW_OPT]);

      if (inputValue?.length > 0) {
        dispatch(
          purchaseProductListAction(
            { field, value: inputValue },
            onSuccess,
            onError
          )
        );
      } else {
        // still return NEW_OPT so menu is never empty
        resolve([NEW_OPT]);
      }
    });

  // ✅ Form initialValues (include dynamic sys fields for existing + initial new row)
  const initialValues = useMemo(() => {
    const mapping_data = {};
    const sysFields = {};

    // existing rows
    Object.values(vendorData).forEach((r) => {
      const k = r.vendor_item_code; // stable key
      mapping_data[k] = {
        rowKey: k,
        vendor_item_code: r.vendor_item_code,
        vendor_item_name: r.vendor_item_name,
        vendor_uoms: r.vendor_uoms || [],
        system_item_id: "",     // ✅ store item_id here
        system_item_code: "",
        system_item_name: "",
        mapping: [],
      };
      sysFields[`sys_code_${k}`] = null;
      sysFields[`sys_name_${k}`] = null;
    });

    // first blank new row
    const firstNewKey = "new_1";
    mapping_data[firstNewKey] = {
      rowKey: firstNewKey,
      vendor_item_code: "",
      vendor_item_name: "",
      vendor_uoms: [],
      system_item_id: "",
      system_item_code: "",
      system_item_name: "",
      mapping: [],
    };
    sysFields[`sys_code_${firstNewKey}`] = null;
    sysFields[`sys_name_${firstNewKey}`] = null;

    return {
      cmr_code: viewVendor ? { label: viewVendor.cmr_code, value: viewVendor.cmr_id } : null,
      cmr_name: viewVendor ? { label: viewVendor.cmr_name, value: viewVendor.cmr_id } : null,
      cmr_id: viewVendor?.cmr_id || null,
      mapping_data,
      ...sysFields,
    };
  }, [vendorData, viewVendor, type]);

  const [filterData, setFilterData] = useState({ sort_by: null, sort_order: "ASC" });
  const paginationProps = useMemo(
    () => ({ page: 1, limit: 10, total: 0, onPageChange: () => {}, onLimitChange: () => {} }),
    []
  );

  const tableHeading = useMemo(
    () => [
      { label: "Vendor Item Code", value: "vendor_item_code", minWidth: "170", alignment: "left", display: false },
      { label: "Vendor Item Name", value: "vendor_item_name", minWidth: "220", alignment: "left", display: false },
      { label: "System Item Code", value: "system_item_code", minWidth: "220", alignment: "left", display: false },
      { label: "System Item Name", value: "system_item_name", minWidth: "240", alignment: "left", display: false },
      { label: "Vendor UOM", value: "vendor_uom", minWidth: "200", alignment: "left", display: false },
      { label: "System Item UOM", value: "system_item_uom", minWidth: "280", alignment: "left", display: false },
    ],
    []
  );

  // Stable handler shared by every UOM cell (add + edit). Keeping it stable lets
  // the memoized UOM cells skip re-rendering on unrelated keystrokes.
  const openUomModalFor = useCallback((rowKey, uoms) => {
    // Block UOM mapping until this row has BOTH vendor item code and a mapped
    // system item — there is nothing meaningful to map a UOM against otherwise.
    const row = mappingDataRef.current?.[rowKey] || {};
    const hasVendorCode = (row.vendor_item_code || rowKey || "").toString().trim();
    const hasSystemCode =
      !!row.system_item_id || (!!row.system_item_code && !!row.system_item_name);

    if (!hasVendorCode || !hasSystemCode) {
      errorToast("Please map Vendor Code and System Code before mapping UOM.");
      return;
    }

    setSelectedVendorRowKey(rowKey);
    setSelectedVendorUOMS(uoms);
    setOpenUom(true);
  }, []);

  // Fetch the available UOMs for a selected system item and store them against
  // the row. The dropdown later shows only these (item_id -> uom id, alt_uom -> name).
  const fetchItemUoms = useCallback(
    (rowKey, item) => {
      if (!rowKey || !item?.item_id) return;
      dispatch(
        purchaseProductDetailsAction(
          { item_id: item.item_id, item_uom: item.item_uom || "" },
          (res) => {
            const list = (res?.data?.uom_dropdown || []).map((u) => ({
              item_id: u.item_id, // treated as the UOM id
              alt_uom: u.alt_uom, // UOM name
            }));
            setRowUomOptions((prev) => ({ ...prev, [rowKey]: list }));
          },
          () => {
            setRowUomOptions((prev) => ({ ...prev, [rowKey]: [] }));
          }
        )
      );
    },
    [dispatch]
  );

  const buildPayload = (values) => {
    const data = Object.values(values.mapping_data || {}).filter((r) => {
      const hasMappings = (r.vendor_uoms || []).some((vu) => uomMemo[vu]?.item_id);
      return (
        (r?.vendor_item_code || "").toString().trim() &&
        (r.system_item_id || r.system_item_code || r.system_item_name || hasMappings)
      );
    });

    return {
      vendor_code: values?.cmr_code?.label || "",
      vendor_name: values?.cmr_name?.label || "",
      vendor_id: values?.cmr_id || "",
      data: data.map((r) => {
        const mapping = (r.vendor_uoms || [])
          .map((vendor_uom) => {
            const memo = uomMemo[vendor_uom];
            if (!memo?.item_id) return null;
            return {
              vendor_uom,
              system_uom: memo.uom_name,
              system_uom_id: memo.item_id,
              uom_mapping_id: memo.uom_mapping_id,
            };
          })
          .filter(Boolean);
        return {
          vendor_item_code: r.vendor_item_code,
          vendor_item_name: r.vendor_item_name,
          system_item_id: r.system_item_id,
          system_item_code: r.system_item_code,
          system_item_name: r.system_item_name,
          mapping,
        };
      }),
    };
  };

  return (
    <div
      ref={rootRef}
      className="flex-1 min-h-0 overflow-hidden"
      style={availHeight ? { height: availHeight } : undefined}
    >
      <Formik initialValues={initialValues} enableReinitialize onSubmit={() => {}}>
        {({ values, setFieldValue, resetForm }) => {
          // keep the ref in sync so openUomModalFor validates against current data
          mappingDataRef.current = values.mapping_data || {};

          // Clear the entire form (Formik fields + component state) after a
          // successful vendor mapping save.
          const clearForm = () => {
            resetForm();
            setUploadedVendorData({});
            setUomMemo({});
            setSelectedVendorUOMS([]);
            setRowUomOptions({});
            setNewRows([{ rowKey: "new_1", vendor_item_code: "", vendor_item_name: "" }]);
            setNewRowSeq(2);
          };

          const setRow = (rowKey, patch) => {
            const prev = values.mapping_data?.[rowKey] || { rowKey };
            setFieldValue("mapping_data", {
              ...(values.mapping_data || {}),
              [rowKey]: { ...prev, ...patch },
            });
          };

          // ✅ Build a fresh blank mapping row object
          const blankMappingRow = (rowKey) => ({
            rowKey,
            vendor_item_code: "",
            vendor_item_name: "",
            vendor_uoms: [],
            system_item_id: "",
            system_item_code: "",
            system_item_name: "",
            mapping: [],
          });

          // ✅ new row vendor_item_code change:
          // - when filled in last row => add a new blank row
          // - when cleared => remove that row (but keep at least one blank row)
          //
          // IMPORTANT: compute the next mapping_data once and write it with a
          // single setFieldValue. Issuing several setFieldValue("mapping_data")
          // calls in one handler each spread the same stale `values.mapping_data`
          // snapshot, so all but the last write get discarded — which dropped the
          // typed vendor_item_code for most rows and broke the stat counters.
          const onNewVendorCodeChange = (rowKey, val) => {
            const trimmed = (val || "").toString();

            const idx = newRows.findIndex((r) => r.rowKey === rowKey);
            if (idx === -1) return;
            const isLast = idx === newRows.length - 1;

            const nextMapping = { ...(values.mapping_data || {}) };
            const ensure = (k) => {
              if (!nextMapping[k]) nextMapping[k] = blankMappingRow(k);
            };

            ensure(rowKey);
            nextMapping[rowKey] = { ...nextMapping[rowKey], vendor_item_code: trimmed };

            let nextRows = newRows.map((r) =>
              r.rowKey === rowKey ? { ...r, vendor_item_code: trimmed } : r
            );

            if (isLast && trimmed.trim()) {
              // user typed into the last blank row => append another blank row
              const newKey = `new_${newRowSeq}`;
              setNewRowSeq((x) => x + 1);
              ensure(newKey);
              nextRows = [...nextRows, { rowKey: newKey, vendor_item_code: "", vendor_item_name: "" }];
            } else if (!trimmed.trim() && !isLast) {
              // code cleared on a non-last row => remove it
              nextRows = nextRows.filter((r) => r.rowKey !== rowKey);
              delete nextMapping[rowKey];
              setFieldValue(`sys_code_${rowKey}`, null);
              setFieldValue(`sys_name_${rowKey}`, null);

              if (nextRows.length === 0) {
                const fallbackKey = `new_${newRowSeq}`;
                setNewRowSeq((x) => x + 1);
                nextMapping[fallbackKey] = blankMappingRow(fallbackKey);
                nextRows = [{ rowKey: fallbackKey, vendor_item_code: "", vendor_item_name: "" }];
              }
            }

            setNewRows(nextRows);
            setFieldValue("mapping_data", nextMapping);
          };

          const onNewVendorNameChange = (rowKey, val) => {
            setNewRows((prev) =>
              prev.map((r) => (r.rowKey === rowKey ? { ...r, vendor_item_name: val } : r))
            );

            const nextMapping = { ...(values.mapping_data || {}) };
            if (!nextMapping[rowKey]) nextMapping[rowKey] = blankMappingRow(rowKey);
            nextMapping[rowKey] = { ...nextMapping[rowKey], vendor_item_name: val };
            setFieldValue("mapping_data", nextMapping);
          };

          const renderSystemCodeField = (rowKey) => (
            <div className="w-[200px]" onClick={(e) => e.stopPropagation()}>
              <SearchField
                type="text"
                label=""
                isLabelReq={false}
                isRequired={false}
                disabled={disabled}
                menuPortalTarget={document.body}
                name={`sys_code_${rowKey}`}
                placeholder="Select System Code"
                lower={true}
                // ✅ prevent SearchField NoOptions navigate
                showCreateCTA={false}
                noOptionsText="Type to search"
                isValidNewOption={false}
                loadOptions={(e) => loadSystemOptions(e, "item_code")}
                onChange={(opt, actionMeta) => {
                  if (opt?.value === "new_customer") {
                    setSelectedVendorRowKey(rowKey);
                    setOpen(true);
                    return;
                  }
                  if (actionMeta?.action === "clear") {
                    setFieldValue(`sys_code_${rowKey}`, null);
                    setFieldValue(`sys_name_${rowKey}`, null);
                    setRow(rowKey, { system_item_id: "", system_item_code: "", system_item_name: "" });
                    setRowUomOptions((prev) => ({ ...prev, [rowKey]: [] }));
                    return;
                  }
                  if (!opt?.raw) return;

                  setFieldValue(`sys_code_${rowKey}`, opt);
                  setFieldValue(`sys_name_${rowKey}`, {
                    value: opt.raw.item_id,
                    label: opt.raw.item_name,
                    raw: opt.raw,
                  });

                  setRow(rowKey, {
                    system_item_id: opt.raw.item_id,         // ✅ store item_id
                    system_item_code: opt.raw.item_code || "",
                    system_item_name: opt.raw.item_name || "",
                  });

                  // fetch this item's available UOMs for the row's UOM dropdown
                  fetchItemUoms(rowKey, opt.raw);
                }}
              />
            </div>
          );

          const renderSystemNameField = (rowKey) => (
            <div className="w-[220px]" onClick={(e) => e.stopPropagation()}>
              <SearchField
                type="text"
                label=""
                isRequired={false}
                isLabelReq={false}
                disabled={disabled}
                menuPortalTarget={document.body}
                name={`sys_name_${rowKey}`}
                placeholder="Select System Name"
                lower={false}
                // ✅ prevent SearchField NoOptions navigate
                showCreateCTA={false}
                noOptionsText="Type to search"
                isValidNewOption={false}
                loadOptions={(e) => loadSystemOptions(e, "item_name")}
                onChange={(opt, actionMeta) => {
                  if (opt?.value === "new_customer") {
                    setSelectedVendorRowKey(rowKey);
                    setOpen(true);
                    return;
                  }
                  if (actionMeta?.action === "clear") {
                    setFieldValue(`sys_code_${rowKey}`, null);
                    setFieldValue(`sys_name_${rowKey}`, null);
                    setRow(rowKey, { system_item_id: "", system_item_code: "", system_item_name: "" });
                    setRowUomOptions((prev) => ({ ...prev, [rowKey]: [] }));
                    return;
                  }
                  if (!opt?.raw) return;

                  setFieldValue(`sys_name_${rowKey}`, opt);
                  setFieldValue(`sys_code_${rowKey}`, {
                    value: opt.raw.item_id,
                    label: opt.raw.item_code,
                    raw: opt.raw,
                  });

                  setRow(rowKey, {
                    system_item_id: opt.raw.item_id,         // ✅ store item_id
                    system_item_code: opt.raw.item_code || "",
                    system_item_name: opt.raw.item_name || "",
                  });

                  // fetch this item's available UOMs for the row's UOM dropdown
                  fetchItemUoms(rowKey, opt.raw);
                }}
              />
            </div>
          );

          // ✅ Build table rows: existing vendor rows + dynamic new rows
          const tableBodyData = [];

          // existing rows
          Object.values(vendorData).forEach((r) => {
            const rowKey = r.vendor_item_code;
            const uoms = values.mapping_data?.[rowKey]?.vendor_uoms || r.vendor_uoms || EMPTY_UOMS;

            tableBodyData.push({
              id: rowKey,
              vendor_item_code: <span>{r.vendor_item_code}</span>,
              vendor_item_name: <span>{r.vendor_item_name}</span>,
              system_item_code: renderSystemCodeField(rowKey),
              system_item_name: renderSystemNameField(rowKey),
              vendor_uom: <VendorUomCell rowKey={rowKey} uoms={uoms} allowAdd={false} onAdd={openUomModalFor} />,
              system_item_uom: (
                <SystemUomCell rowKey={rowKey} uoms={uoms} uomMemo={uomMemo} onEdit={openUomModalFor} />
              ),
            });
          });

          // dynamic new rows (the “new line below”, repeating behavior)
          newRows.forEach((nr) => {
            const rowKey = nr.rowKey;
            const vlist = values.mapping_data?.[rowKey]?.vendor_uoms || nr.vendor_uoms || EMPTY_UOMS;

            tableBodyData.push({
              id: rowKey,
              vendor_item_code: (
                <div className="w-[170px]" onClick={(e) => e.stopPropagation()}>
                  <input
                    className={`${INPUT_CLS} opacity-70`}
                    placeholder="Enter Item Code"
                    value={nr.vendor_item_code}
                    onChange={(e) => onNewVendorCodeChange(rowKey, e.target.value)}
                  />
                </div>
              ),
              vendor_item_name: (
                <div className="w-[220px]" onClick={(e) => e.stopPropagation()}>
                  <input
                    className={`${INPUT_CLS} opacity-70`}
                    placeholder="Enter Item Name"
                    value={nr.vendor_item_name}
                    onChange={(e) => onNewVendorNameChange(rowKey, e.target.value)}
                  />
                </div>
              ),
              system_item_code: renderSystemCodeField(rowKey),
              system_item_name: renderSystemNameField(rowKey),
              vendor_uom: <VendorUomCell rowKey={rowKey} uoms={vlist} allowAdd onAdd={openUomModalFor} />,
              system_item_uom: (
                <SystemUomCell rowKey={rowKey} uoms={vlist} uomMemo={uomMemo} onEdit={openUomModalFor} />
              ),
            });
          });

          return (
            <div className="px-5 mt-4 w-full h-full flex flex-col min-h-0 overflow-hidden">
              {/* HEADER */}
              <div className="shrink-0 pb-3">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                  <div className="w-full xl:flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                      {/* Vendor Code */}
                      <div className="min-w-0">
                        <SearchField
                          type="text"
                          label="Vendor Code"
                          disabled={disabled}
                          menuPortalTarget={document.body}
                          isValidNewOption={true}
                          autoFocus={true}
                          name="cmr_code"
                          placeholder="Enter Vendor Code"
                          onCreateOption={() => {}}
                          loadOptions={(e) => loadVendorOptions(e, "cmr_code")}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta?.action === "clear") {
                              setFieldValue("cmr_code", null);
                              setFieldValue("cmr_name", null);
                              setFieldValue("cmr_id", null);
                              return;
                            }
                            if (selectedOption?.value === "new_customer") {
                              navigate("/admin/add-customer");
                              return;
                            }
                            const customer = vendorOptionsData.find((x) => x.cmr_id === selectedOption?.value);
                            if (!customer) return;

                            setFieldValue("cmr_code", { label: customer.cmr_code, value: customer.cmr_id });
                            setFieldValue("cmr_name", { label: customer.cmr_first_name, value: customer.cmr_id });
                            setFieldValue("cmr_id", customer.cmr_id);
                          }}
                        />
                      </div>

                      {/* Vendor Name */}
                      <div className="min-w-0">
                        <SearchField
                          type="text"
                          label="Vendor Name"
                          disabled={disabled}
                          menuPortalTarget={document.body}
                          isValidNewOption={true}
                          name="cmr_name"
                          placeholder="Enter Vendor Name"
                          onCreateOption={() => {}}
                          loadOptions={(e) => loadVendorOptions(e, "cmr_first_name")}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta?.action === "clear") {
                              setFieldValue("cmr_code", null);
                              setFieldValue("cmr_name", null);
                              setFieldValue("cmr_id", null);
                              return;
                            }
                            if (selectedOption?.value === "new_customer") {
                              navigate("/admin/add-customer");
                              return;
                            }
                            const customer = vendorOptionsData.find((x) => x.cmr_id === selectedOption?.value);
                            if (!customer) return;

                            setFieldValue("cmr_code", { label: customer.cmr_code, value: customer.cmr_id });
                            setFieldValue("cmr_name", { label: customer.cmr_first_name, value: customer.cmr_id });
                            setFieldValue("cmr_id", customer.cmr_id);
                          }}
                        />
                      </div>

                      <div className="pt-[22px] sm:pt-[24px]">
                        <>
                    <input
                      type="file"
                      accept=".csv,.json"
                      className="hidden"
                      id="csv-upload"
                      disabled={!values?.cmr_id}
                      onChange={(e) => handleUploadFile(e, values)}
                    />

                    <label
                      htmlFor="csv-upload"
                      // Import is allowed only after a vendor is selected.
                      title={!values?.cmr_id ? "Select a vendor first" : "Import CSV / JSON"}
                      className={!values?.cmr_id ? "pointer-events-none" : ""}
                    >
                      <div
                        className={`flex items-center justify-between px-[12px] h-[44px] w-full sm:w-auto
                          whitespace-nowrap rounded-[8px] gap-[10px]
                          bg-[var(--import-button-bg)]
                          border border-[var(--import-button-border)]
                          hover:bg-[var(--import-button-bg)]
                          text-[var(--import-button-fg)]
                          ${!values?.cmr_id ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        {/* Left side: Excel/JSON icons + text */}
                        <div className="flex items-center gap-[8px]">
                          <img width={20} height={20} src={excelImg} alt="excel-icon" />
                          <FileJson width="20px" height="20px" className="text-[var(--import-button-fg)]" />
                          <span>Import CSV / JSON</span>
                        </div>

                        <DownloadIcon
                          onClick={(e) => {
                            e.preventDefault();
                            // same template as the GRN import (matches the shared verify API)
                            downloadAllTemplates(
                              initUploadDocumentsGrn.map(
                                ({ documentType, expectedHeaders }) => ({
                                  documentType,
                                  expectedHeaders,
                                })
                              )
                            );
                          }}
                          className="w-5 h-5 text-[var(--import-button-fg)]"
                        />
                      </div>
                    </label>
                    <span title="Download JSON Template">
                      <button
                        type="button"
                        onClick={() => {
                          downloadAllTemplatesJson(
                            initUploadDocumentsGrn.map(
                              ({ documentType, expectedHeaders }) => ({
                                documentType,
                                expectedHeaders,
                              })
                            )
                          );
                        }}
                        className="flex items-center justify-center px-[12px] h-[44px]
                          rounded-[8px] bg-[var(--import-button-bg)] cursor-pointer
                          border border-[var(--import-button-border)]
                          hover:bg-[var(--import-button-bg)]
                          text-[var(--import-button-fg)]"
                      >
                        <FileJson className="w-5 h-5 text-[var(--import-button-fg)]" />
                      </button>
                    </span>
                  </>
                      </div>

                      <div className="pt-[22px] sm:pt-[24px]">
                        <AddButton
                          className="w-full sm:w-auto flex items-center justify-center px-[12px] h-[44px] max-h-[44px] whitespace-nowrap border-radius gap-[8px] bg-[var(--success-color,#16a34a)] cursor-pointer text-white border font-bold border-[var(--success-color,#16a34a)] hover:bg-white hover:text-[var(--success-color,#16a34a)]"
                          icon="none"
                          onclkFn={() => {
                            // 1) Vendor must be selected (priority)
                            if (!values?.cmr_id) {
                              errorToast("Please select Vendor Code and Vendor Name before saving.");
                              return;
                            }

                            const allRows = Object.values(values.mapping_data || {});

                            // 2) Ensure system mapping (name/code/item) exists for rows that have vendor item code/name
                            const rowsMissingSystem = allRows.filter((r) => {
                              const hasVendor = (r?.vendor_item_code || r?.vendor_item_name || "").toString().trim();
                              if (!hasVendor) return false;
                              // require system_item_id (preferred) or both system_item_code and system_item_name
                              return !(r.system_item_id || (r.system_item_code && r.system_item_name));
                            });
                            if (rowsMissingSystem.length > 0) {
                              const codes = rowsMissingSystem.map((r) => r.vendor_item_code || r.vendor_item_name || r.rowKey).join(", ");
                              errorToast(`System item is not mapped for vendor rows: ${codes}`);
                              return;
                            }

                            // 3) Ensure vendor UOMs are mapped for rows that have vendor item code and vendor uoms
                            const rowsToValidate = allRows.filter((r) => (r?.vendor_item_code || "").toString().trim() && (r.vendor_uoms || []).length > 0);
                            const unmapped = rowsToValidate.filter((r) => (r.vendor_uoms || []).some((vu) => !uomMemo[vu]?.item_id));
                            if (unmapped.length > 0) {
                              const codes = unmapped.map((r) => r.vendor_item_code || r.vendor_item_name || "—").join(", ");
                              errorToast(`The following vendor code UOM is not mapped: ${codes}`);
                              return;
                            }

                            const payload = buildPayload(values);
                            console.log(payload);
                            console.log(JSON.stringify(payload, null, 2));
                            dispatch(
                              VendorMappingAction(
                                payload,
                                () => {
                                  successToast("Mapping Successful");
                                  clearForm();
                                },
                                () => {
                                  errorToast("Mapping Unsuccessful");
                                }
                              )
                            );
                          }}
                          text="Save"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="w-full xl:w-auto">
                    <div className="flex flex-col items-stretch sm:flex-row gap-3 sm:flex-wrap xl:flex-nowrap xl:justify-end">
                      {(() => {
                        const { totalItems, itemsMapped, itemsPending } = computeStats(values.mapping_data, uomMemo);
                        return (
                          <>
                            <StatCard icon={Database} value={itemsMapped} label="Items Mapped" tone="success" />
                            <StatCard icon={Hourglass} value={itemsPending} label="Items Pending" tone="danger" />
                            <StatCard icon={Layers} value={totalItems} label="Total Items" tone="info" />
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* SIDE PANEL */}
              <VMProductSidePanel
                open={open}
                setOpen={setOpen}
                selectedVendorItemId={selectedVendorRowKey}
                uomOptionsFromParent={uomOptions}
                onProductCreated={(item) => {
                  if (!selectedVendorRowKey || !item) return;
                  const raw = { item_id: item.item_id, item_code: item.item_code || "", item_name: item.item_name || "" };
                  setRow(selectedVendorRowKey, {
                    system_item_id: raw.item_id,
                    system_item_code: raw.item_code,
                    system_item_name: raw.item_name,
                  });
                  setFieldValue(`sys_code_${selectedVendorRowKey}`, {
                    value: raw.item_id,
                    label: raw.item_code,
                    raw,
                  });
                  setFieldValue(`sys_name_${selectedVendorRowKey}`, {
                    value: raw.item_id,
                    label: raw.item_name,
                    raw,
                  });
                  // fetch the new product's available UOMs for its UOM dropdown
                  fetchItemUoms(selectedVendorRowKey, { item_id: item.item_id, item_uom: item.item_uom || "" });
                  setOpen(false);
                }}
              />
              <SelectUOMModal
                open={openUom}
                setOpen={setOpenUom}
                selectedVendorUOMS={selectedVendorUOMS}
                uom={rowUomOptions[selectedVendorRowKey] || []}
                uomMemo={uomMemo}
                setUomMemo={(updater) =>
                  setUomMemo((prev) => (typeof updater === "function" ? updater(prev) : updater))
                }
                onVendorUomsChange={(newVendorUoms) => {
                  if (!selectedVendorRowKey) return;
                  setRow(selectedVendorRowKey, { vendor_uoms: newVendorUoms });
                }}
              />
              {/* TABLE */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <DynamicTransactionTable
                  tableHeading={tableHeading}
                  tableBodyData={tableBodyData}
                  label="Items"
                  is_checkbox_req={false}
                  menu={false}
                  filterSec={false}
                  filter={false}
                  showFilter={false}
                  showClear={false}
                  showFilterArrows={false}
                  addNew={false}
                  paginationProps={paginationProps}
                  filterData={filterData}
                  setFilterData={setFilterData}
                  onClickItemFn={() => {}}
                  showPagination={false}
                  footerVisible={false}
                />
              </div>
            </div>
          );
        }}
      </Formik>
    </div>
  );
};

export default VendorItemMapping;
